import { useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { PackageCheck, Table, Database, Upload } from 'lucide-react';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../../ui/tabs';
import { useAuth } from '../../../contexts/AuthContext';
import StockPlanningTab from '../../call-off/stock-planning/StockPlanningTab';
import SupplierStockPlanningTab from '../../call-off/stock-planning/SupplierStockPlanningTab';
import CallOffManagementTab from '../../call-off/management/CallOffManagementTab';
import PartRepositoryTab from '../../call-off/parts/PartRepositoryTab';
import StockUploadTab from '../../call-off/stock-upload/StockUploadTab';

const CUSTOMER_TABS = [
  { id: 'stock-planning', label: 'Stock Planning', icon: Table },
  { id: 'management', label: 'Call Off Management', icon: PackageCheck },
  { id: 'parts', label: 'Parts List', icon: Database },
] as const;

const SUPPLIER_TABS = [
  { id: 'management', label: 'Call Off Management', icon: PackageCheck },
  { id: 'supplier-stock-planning', label: 'Stock Planning', icon: Table },
  { id: 'stock-upload', label: 'Stock Upload', icon: Upload },
] as const;

const LOGISTICS_TABS = [
  { id: 'management', label: 'Call Off Management', icon: PackageCheck },
] as const;

type TabId = 'stock-planning' | 'supplier-stock-planning' | 'management' | 'parts' | 'stock-upload';

/**
 * Call Off tab container rendered inside the Purchasing page.
 * Customers see 3 inner tabs (Stock Planning, Management, Parts).
 * Suppliers see 2 inner tabs (Management, Stock Upload).
 * Logistics users see 1 inner tab (Management) — call-offs further filtered
 * to dispatched/delivered by CallOffViewSet.get_queryset().
 */
export default function CallOffTab() {
  const { user } = useAuth();
  const [searchParams] = useSearchParams();
  const isLogistics = user?.company_type === 'logistics';
  const isSupplier = user?.platform === 'SUPPLIER';
  const tabs = isLogistics ? LOGISTICS_TABS : isSupplier ? SUPPLIER_TABS : CUSTOMER_TABS;
  // Auto-switch to management sub-tab when navigating from a notification
  const hasCallOffId = searchParams.has('calloff_id');
  const defaultTab: TabId = isLogistics || isSupplier || hasCallOffId ? 'management' : 'stock-planning';
  const [activeTab, setActiveTab] = useState<TabId>(defaultTab);

  return (
    <div className="space-y-4">
      <Tabs value={activeTab} onValueChange={(v) => setActiveTab(v as TabId)}>
        <TabsList>
          {tabs.map((tab) => {
            const Icon = tab.icon;
            return (
              <TabsTrigger key={tab.id} value={tab.id} className="flex items-center gap-2">
                <Icon className="h-4 w-4" />
                {tab.label}
              </TabsTrigger>
            );
          })}
        </TabsList>

        <TabsContent value="stock-planning" className="mt-4">
          <StockPlanningTab />
        </TabsContent>

        <TabsContent value="supplier-stock-planning" className="mt-4">
          <SupplierStockPlanningTab />
        </TabsContent>

        <TabsContent value="management" className="mt-4">
          <CallOffManagementTab />
        </TabsContent>

        <TabsContent value="parts" className="mt-4">
          <PartRepositoryTab />
        </TabsContent>

        <TabsContent value="stock-upload" className="mt-4">
          <StockUploadTab />
        </TabsContent>
      </Tabs>
    </div>
  );
}
